import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAbortableLoad, isCanceled } from '../hooks/useAbortableLoad';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, ShieldAlert, Unlock, RefreshCw, CheckCircle2 } from 'lucide-react';

const ROLE_COLORS = {
  ADMINISTRADOR: 'bg-purple-100 text-purple-800',
  SUPERVISOR: 'bg-blue-100 text-blue-800',
  LEGAL: 'bg-teal-100 text-teal-800',
  USUARIO: 'bg-slate-100 text-slate-800',
};

function formatDuracion(seg) {
  if (!seg || seg <= 0) return '—';
  const m = Math.floor(seg / 60);
  const s = seg % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  }
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatFecha(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-PE'); } catch { return iso; }
}

const CuentasBloqueadasPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cuentas, setCuentas] = useState([]);
  const [desbloqueando, setDesbloqueando] = useState(null);

  const fetchCuentas = async (signal) => {
    setLoading(true);
    try {
      const r = await api.get('/usuarios/bloqueados', { signal });
      setCuentas(r.data || []);
    } catch (e) {
      if (isCanceled(e)) return;
      toast.error('No se pudieron cargar las cuentas bloqueadas');
    } finally {
      setLoading(false);
    }
  };

  useAbortableLoad((signal) => fetchCuentas(signal), []);

  const desbloquear = async (c) => {
    if (!c.usuario_id) {
      toast.error('Esta cuenta no está asociada a un usuario de la empresa.');
      return;
    }
    if (!window.confirm(`¿Desbloquear la cuenta de ${c.nombre || c.correo}?`)) return;
    setDesbloqueando(c.usuario_id);
    try {
      await api.post(`/usuarios/${c.usuario_id}/desbloquear`);
      toast.success('Cuenta desbloqueada');
      setCuentas((prev) => prev.filter((x) => x.correo !== c.correo));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al desbloquear');
    } finally {
      setDesbloqueando(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="cuentas-bloqueadas-page">
      {/* Header + volver */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <button
            onClick={() => navigate('/admin/usuarios')}
            data-testid="volver-usuarios-link"
            className="inline-flex items-center text-sm text-slate-500 hover:text-slate-800 mb-1"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver a Usuarios
          </button>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-amber-600" /> Cuentas bloqueadas
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Cuentas bloqueadas por intentos fallidos de inicio de sesión. Revisa el detalle antes de desbloquear.
          </p>
        </div>
        <Button variant="outline" onClick={fetchCuentas} data-testid="refrescar-bloqueadas-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refrescar
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando…
        </div>
      ) : cuentas.length === 0 ? (
        <Alert data-testid="sin-bloqueadas">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>No hay cuentas bloqueadas en este momento.</AlertDescription>
        </Alert>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Tiempo restante</TableHead>
                  <TableHead>Nivel</TableHead>
                  <TableHead>Último intento</TableHead>
                  <TableHead>Última IP</TableHead>
                  <TableHead className="text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cuentas.map((c) => (
                  <TableRow key={c.correo} data-testid={`bloqueada-row-${c.correo}`}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{c.nombre || '—'}</div>
                      <div className="text-xs text-slate-500">{c.correo}</div>
                    </TableCell>
                    <TableCell>
                      {c.rol ? <Badge className={ROLE_COLORS[c.rol] || 'bg-slate-100 text-slate-800'}>{c.rol}</Badge> : '—'}
                    </TableCell>
                    <TableCell>
                      {c.bloqueo_permanente ? (
                        <Badge className="bg-red-100 text-red-800">Permanente</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800">Temporal</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {c.bloqueo_permanente ? '—' : formatDuracion(c.segundos_restantes)}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{c.bloqueo_nivel}</TableCell>
                    <TableCell className="text-sm text-slate-600">{formatFecha(c.ultimo_intento_at)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{c.ultimo_ip || '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => desbloquear(c)}
                        disabled={desbloqueando === c.usuario_id || !c.usuario_id}
                        data-testid={`desbloquear-btn-${c.correo}`}
                      >
                        {desbloqueando === c.usuario_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <><Unlock className="w-4 h-4 mr-1" /> Desbloquear</>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CuentasBloqueadasPage;
